import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type { ProjectManifest, RecentProject } from '../../core/types/project.types';

interface ProjectState {
  // Current project
  currentProject: ProjectManifest | null;
  currentProjectPath: string | null;

  // Recent projects
  recentProjects: RecentProject[];
  isLoadingRecent: boolean;

  // Dirty tracking
  isDirty: boolean;
  lastSavedAt: string | null;
  dirtySubscriptions: (() => void)[];

  // Actions
  setCurrentProject: (project: ProjectManifest | null, path: string | null) => void;
  setRecentProjects: (projects: RecentProject[]) => void;
  setIsLoadingRecent: (loading: boolean) => void;
  addRecentProject: (project: RecentProject) => void;
  removeRecentProject: (path: string) => void;
  clearRecentProjects: () => void;

  // Dirty tracking actions
  setDirty: (dirty: boolean) => void;
  markDirty: () => void;
  markSaved: () => void;
  setupDirtyTracking: () => Promise<void>;
  cleanupDirtyTracking: () => void;
}

export const useProjectStore = create<ProjectState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Current project
      currentProject: null,
      currentProjectPath: null,

      // Recent projects
      recentProjects: [],
      isLoadingRecent: false,

      // Dirty tracking
      isDirty: false,
      lastSavedAt: null,
      dirtySubscriptions: [],

      // Actions
      setCurrentProject: (project, path) =>
        set({ currentProject: project, currentProjectPath: path }),

      setRecentProjects: (projects) =>
        set({ recentProjects: projects }),

      setIsLoadingRecent: (loading) =>
        set({ isLoadingRecent: loading }),

      addRecentProject: (project) =>
        set((state) => ({
          recentProjects: [
            project,
            ...state.recentProjects.filter((p) => p.path !== project.path),
          ].slice(0, 10),
        })),

      removeRecentProject: (path) =>
        set((state) => ({
          recentProjects: state.recentProjects.filter((p) => p.path !== path),
        })),

      clearRecentProjects: () =>
        set({ recentProjects: [] }),

      // Dirty tracking actions
      setDirty: (dirty) => set({ isDirty: dirty }),

      markDirty: () => {
        const state = get();
        // Only mark dirty if we have an open project and not already dirty
        if (state.currentProject && !state.isDirty) {
          set({ isDirty: true });
        }
      },

      markSaved: () =>
        set({
          isDirty: false,
          lastSavedAt: new Date().toISOString(),
        }),

      setupDirtyTracking: async () => {
        const subscriptions: (() => void)[] = [];
        const { markDirty } = get();

        // Helper to create a subscription with state comparison
        // Uses JSON.stringify for deep equality check on selected state
        const createSubscription = <T, S>(
          store: { subscribe: (listener: (state: T, prevState: T) => void) => () => void; getState: () => T },
          selector: (state: T) => S
        ): (() => void) => {
          let prevSelected = JSON.stringify(selector(store.getState()));
          return store.subscribe((state, prevState) => {
            const newSelected = JSON.stringify(selector(state));
            if (newSelected !== prevSelected) {
              prevSelected = newSelected;
              markDirty();
            }
          });
        };

        // Use dynamic imports to avoid circular dependencies
        // We subscribe to changes in stores that affect project state
        // Import all stores in parallel using Promise.all
        const [
          { useSceneStore },
          { useSceneViewStore },
          { useScriptEditorStore },
          { useAnimationStore },
        ] = await Promise.all([
          import('./sceneStore'),
          import('./sceneViewStore'),
          import('./scriptEditorStore'),
          import('./animationStore'),
        ]);

        // Subscribe to sceneStore changes (gameObjects, rootIds, sceneName)
        subscriptions.push(
          createSubscription(useSceneStore, (state) => ({
            gameObjects: state.gameObjects,
            rootIds: state.rootIds,
            sceneName: state.sceneName,
          }))
        );

        // Subscribe to sceneViewStore changes (viewport settings)
        subscriptions.push(
          createSubscription(useSceneViewStore, (state) => ({
            viewport: state.viewport,
            activeTool: state.activeTool,
          }))
        );

        // Subscribe to scriptEditorStore changes (open tabs, files)
        subscriptions.push(
          createSubscription(useScriptEditorStore, (state) => ({
            openTabs: state.openTabs,
            files: state.files,
          }))
        );

        // Subscribe to animationStore changes (animations)
        subscriptions.push(
          createSubscription(useAnimationStore, (state) => ({
            animations: state.animations,
            selectedAnimationId: state.selectedAnimationId,
          }))
        );

        // Store subscriptions for cleanup
        set({ dirtySubscriptions: subscriptions });
      },

      cleanupDirtyTracking: () => {
        const { dirtySubscriptions } = get();
        // Unsubscribe all
        dirtySubscriptions.forEach((unsubscribe) => unsubscribe());
        set({
          dirtySubscriptions: [],
          isDirty: false,
          lastSavedAt: null,
        });
      },
    })),
    {
      name: 'project-store',
    }
  )
);
